import { UL } from '@wheely/ui-kit';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl, FormattedList } from 'react-intl';
import { useSelector } from 'react-redux';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { getLocalizedCarMakeName } from '@/i18n/getLocalizedCarMakeName';
import { getLocalizedColorName } from '@/i18n/getLocalizedColorName';
import { getModelsByBrand } from '@/modules/car/store/selectors';
import { getCountryCode } from '@/modules/common/store/selectors';
import { CountryCode } from '@/shared/models/country-code';
import { Section } from '@/shared/view/Section';

import s from './styles.module.css';

const DoYouHaveACar: React.FC = () => {
  const intl = useIntl();
  const country = useSelector(getCountryCode);
  const modelsByBrand = useSelector(getModelsByBrand);

  const { contactOperationTeam, faqAcquiringANewVehicle } = useExternalLinks();

  const countryBasedRequirements = useMemo(() => {
    switch (country) {
      case CountryCode.Russia:
        return (
          <FormattedMessage
            defaultMessage="<li>Excellent external and technical condition of the vehicle.</li>"
            id="AFNSHg"
            values={{ li: (text: React.ReactNode) => <li>{text}</li> }}
            description="RU requirements for car  (does your vehicle qualify onbaording step)"
          />
        );

      case CountryCode.France:
        return (
          <FormattedMessage
            defaultMessage="<li>Must have a valid VTC license mentioning plate number.</li>"
            id="UoVWYR"
            values={{ li: (text: React.ReactNode) => <li>{text}</li> }}
            description="FR requirements for car  (does your vehicle qualify onbaording step)"
          />
        );

      default:
        return (
          <FormattedMessage
            defaultMessage="<li>Fully licensed by TfL, Castlepoint, Hertsmere or Royal Borough of Windsor and Maidenhead and covered by commercial insurance.</li>"
            id="80lHib"
            values={{ li: (text: React.ReactNode) => <li>{text}</li> }}
            description="GB requirements for car  (does your vehicle qualify onbaording step)"
          />
        );
    }
  }, [country]);

  return (
    <Section>
      <UL className={s.list}>
        {Array.from(modelsByBrand.entries()).map(([make, models]) =>
          Array.from(models).map(model => (
            <li key={`${make}_${model.model}`}>
              {getLocalizedCarMakeName(make, intl)} {model.model}
              {model.note ? '*' : null}
              {model.generations.length > 0 ? (
                <>
                  {' '}
                  (<FormattedList value={model.generations} style="narrow" type="conjunction" />)
                </>
              ) : null}
              .{' '}
              <FormattedMessage
                defaultMessage="Up to {maxAge, plural, one {# year} other {# years}} old."
                id="r61hCi"
                description="Car age requirements (does your vehicle qualify onbaording step)"
                values={{ maxAge: model.maxAge }}
              />{' '}
              <span className={s.colors}>
                <FormattedMessage
                  defaultMessage="In {listOfColors} colours."
                  id="+zI1x6"
                  description="Car colors requirements (does your vehicle qualify onbaording step)"
                  values={{
                    listOfColors: intl.formatList(
                      model.color.map(color => getLocalizedColorName(color.value, intl)),
                      { type: 'disjunction' },
                    ),
                  }}
                />
              </span>
            </li>
          )),
        )}
        {countryBasedRequirements}
      </UL>
      {country === CountryCode.GreatBritain ? (
        <div className={s.notes}>
          <p className={s.note}>
            <FormattedMessage
              defaultMessage="*If you have an EQE, please get in touch with us at <link></link>."
              id="kYEYox"
              values={{
                link: () => (
                  <a href={`mailto:${contactOperationTeam}`} target="_blank" rel="noreferrer">
                    {contactOperationTeam}
                  </a>
                ),
              }}
              description="GB note for EQE model"
            />
          </p>
          <p className={s.note}>
            <FormattedMessage
              defaultMessage="Don't have a vehicle yet? See <link>here</link> some options that might be interesting to you."
              id="SbCpEF"
              values={{
                link: (text: React.ReactNode) => <a href={faqAcquiringANewVehicle}>{text}</a>,
              }}
              description="GB note for additional options"
            />
          </p>
        </div>
      ) : null}
    </Section>
  );
};

export { DoYouHaveACar };
